import React, { useCallback, useMemo } from "react";
import { connect } from "react-redux";

import notification from "antd/es/notification";
import { defineMessages, useIntl } from "react-intl";

import { EntityTypesSupportingRoutes, Route } from "@mapmycustomers/shared/types/entity";
import { RoutePayload } from "@mapmycustomers/shared/types/entity/Route";
import { nameComparator } from "@mapmycustomers/shared/util/comparator";
import useBoolean from "@mapmycustomers/shared/util/hook/useBoolean";
import useDebouncedCallback from "@mapmycustomers/shared/util/hook/useDebouncedCallback";
import { SelectField } from "@mapmycustomers/ui";

import CreateRoutePureModal from "@app/component/AddToRouteModal/CreateRoutePureModal";
import ButtonLink from "@app/component/ButtonLink";
import defaultFilterOption from "@app/component/input/utils/defaultFilterOption";
import { filterRoutes } from "@app/scene/map/store/actions";
import {
  getAddRecordsAvailableRoutes,
  isAddRecordsInitializing,
} from "@app/scene/map/store/selectors";
import RecordRouteModel from "@app/scene/map/types/RecordRouteModel";
import { isGeocodeMmcLimitReached, isGeocodeOrgLimitReached } from "@app/store/iam";
import { RootState } from "@app/store/rootReducer";
import showGeocodeLimitAlertModal from "@app/util/geocodeLimitAlert/showGeocodeLimitAlertModal";

import styles from "./Routes.module.scss";

const NEW_ROUTE_PREFIX = "new__";

const isRouteId = (value: RecordRouteModel): value is Route["id"] => typeof value === "number";

const mapRouteRecordToValue = (value: RecordRouteModel): string =>
  isRouteId(value) ? value.toString() : `${NEW_ROUTE_PREFIX}${value.name}`;

const messages = defineMessages({
  newRouteButton: {
    id: "map.tool.leadFinder.addRecords.newRouteButtonTitle",
    defaultMessage: "+ Create New Route",
    description: "Create new route button for Add Records",
  },
  newRouteName: {
    id: "map.tool.leadFinder.addRecords.newRouteName",
    defaultMessage: "{name} [NEW]",
    description: "New route name for Add Records",
  },
  placeholder: {
    id: "map.tool.leadFinder.addRecords.routes.placeholder",
    defaultMessage: "Click or type to select routes",
    description: "Select Route placeholder for Add Records",
  },
  routeAddedDescription: {
    id: "map.tool.leadFinder.addRecords.routes.routeAdded.description",
    defaultMessage:
      "Please note, this route will not be saved until Leads are added as Companies or People.",
    description: "Route created description for Add Records",
  },
  routeAddedMessage: {
    id: "map.tool.leadFinder.addRecords.routes.routeAdded.message",
    defaultMessage: "New Route Created",
    description: "Route created message for Add Records",
  },
});

interface Props {
  entityType: EntityTypesSupportingRoutes;
  initializing: boolean;
  isMmcGeocodeLimitReached: boolean;
  isOrgGeocodeLimitReached: boolean;
  onChange?: (value: RecordRouteModel[]) => void;
  onSearch: typeof filterRoutes.request;
  routes: Route[];
  value?: RecordRouteModel[];
}

const Routes: React.FC<Props> = ({
  entityType,
  initializing,
  isMmcGeocodeLimitReached,
  isOrgGeocodeLimitReached,
  onChange,
  onSearch,
  routes,
  value,
}) => {
  const intl = useIntl();
  const [createRouteModalVisible, showCreateRouteModal, hideCreateRouteModal] = useBoolean();

  const handleCreateRouteClick = useCallback(() => {
    if (isMmcGeocodeLimitReached || isOrgGeocodeLimitReached) {
      showGeocodeLimitAlertModal(intl, isMmcGeocodeLimitReached);
    } else {
      showCreateRouteModal();
    }
  }, [intl, isMmcGeocodeLimitReached, isOrgGeocodeLimitReached, showCreateRouteModal]);

  const parsedValue = useMemo(() => (value ?? []).map(mapRouteRecordToValue), [value]);

  const existingRoutes: (Route | RoutePayload)[] = useMemo(() => {
    return [
      ...routes,
      ...(value ?? []).filter((record: RecordRouteModel) => !isRouteId(record)),
    ] as (Route | RoutePayload)[];
  }, [value, routes]);

  const routesOptions = useMemo(
    () =>
      existingRoutes.sort(nameComparator).map((record: Route | RoutePayload) => ({
        label: (record as Route)?.id
          ? record.name
          : intl.formatMessage(messages.newRouteName, { name: record.name }),
        value: mapRouteRecordToValue((record as Route)?.id ?? record),
      })),
    [intl, existingRoutes]
  );

  const handleChange = useCallback(
    (selectedValue: string[]) => {
      const routePayloadByKey = (
        (value ?? []).filter((record: RecordRouteModel) => !isRouteId(record)) as RoutePayload[]
      ).reduce<Record<string, RoutePayload>>(
        (result, record: RoutePayload) => ({
          ...result,
          [mapRouteRecordToValue(record)]: record,
        }),
        {}
      );
      onChange?.(
        selectedValue.map((record) =>
          record.includes(NEW_ROUTE_PREFIX) ? routePayloadByKey[record] : parseInt(record, 10)
        )
      );
    },
    [value, onChange]
  );

  const handleCreateRoute = useCallback(
    (route: RoutePayload) => {
      hideCreateRouteModal();
      notification.success({
        description: intl.formatMessage(messages.routeAddedDescription),
        message: intl.formatMessage(messages.routeAddedMessage),
      });
      onChange?.([...(value ?? []), route]);
    },
    [hideCreateRouteModal, intl, onChange, value]
  );

  const handleSearch = useDebouncedCallback(
    [
      (value: string) => {
        onSearch({ entityType, query: value });
      },
      500,
    ],
    [entityType, onSearch]
  );

  const handleBlur = useCallback(() => {
    onSearch({ entityType });
  }, [entityType, onSearch]);

  return (
    <div>
      <div className={styles.selector}>
        <SelectField<string[]>
          dropdownMatchSelectWidth={false}
          filterOption={defaultFilterOption}
          loading={initializing}
          mode="multiple"
          onBlur={handleBlur}
          onChange={handleChange}
          onSearch={handleSearch}
          options={routesOptions}
          placeholder={intl.formatMessage(messages.placeholder)}
          showSearch
          value={parsedValue}
        />
      </div>
      <ButtonLink className={styles.newRouteBtn} onClick={handleCreateRouteClick}>
        {intl.formatMessage(messages.newRouteButton)}
      </ButtonLink>
      {createRouteModalVisible && (
        <CreateRoutePureModal
          entityType={entityType}
          onCreate={handleCreateRoute}
          onHide={hideCreateRouteModal}
          routes={existingRoutes}
        />
      )}
    </div>
  );
};

const mapStateToProps = (state: RootState) => ({
  initializing: isAddRecordsInitializing(state),
  isMmcGeocodeLimitReached: isGeocodeMmcLimitReached(state),
  isOrgGeocodeLimitReached: isGeocodeOrgLimitReached(state),
  routes: getAddRecordsAvailableRoutes(state),
});

const mapDispatchToProps = {
  onSearch: filterRoutes.request,
};

export default connect(mapStateToProps, mapDispatchToProps)(Routes);
